import React from 'react';
import { ArrowRight, Truck, Home, Box, Clock, Shield, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const HouseholdStorage = () => {
    const benefits = [
        'Cut Moving Time in Half: Unload heavy items straight from truck to unit.',
        'Renovation Friendly: Easy ground-level access for swapping furniture.',
        'Short-Term Flexibility: Month-to-month leases perfect for moving gaps.',
        'Drive-Up Convenience: Park right at your door.'
    ];

    const unitSizes = [
        {
            size: '5\' x 10\'',
            title: 'Decluttering & seasonal',
            description: 'Fits a Queen mattress, dresser, and boxes. (Great for decluttering).'
        },
        {
            size: '10\' x 10\'',
            title: '1-Bedroom Apartment',
            description: 'Fits the contents of a 1-bedroom apartment.'
        },
        {
            size: '10\' x 15\'',
            title: 'Large Master Bedroom',
            description: 'Fits contents of a 2-bedroom apartment.'
        },
        {
            size: '10\' x 20\'',
            title: 'The "Home Mover" Favorite',
            description: 'Fits the contents of a 3-bedroom home. (Single-car garage size).'
        },
        {
            size: '10\' x 30\'',
            title: 'Multigenerational Home',
            description: 'Fits contents of a 4-5 bedroom home. Extra large garage capacity.'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <section className="bg-green-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <SEO
                        title="Drive-Up Household Storage in Lewisville, TX | Interskate Self Storage"
                        description="Ground-level drive-up household storage in Lewisville, TX. Perfect for moving, decluttering, and home renovation. No elevators. Park right at your unit door."
                        canonical="/household-storage-lewisville"
                    />
                    <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="household-storage-title">
                        Drive-Up Household Storage in Lewisville
                    </h1>
                    <p className="text-xl text-green-100 max-w-3xl mx-auto mb-6">
                        The easiest move you’ll ever make. Park right at your door.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                if (window.gtag) {
                                    window.gtag('event', 'conversion_intent', {
                                        'event_category': 'lead',
                                        'event_label': 'household_hero_rates',
                                        'facility_name': 'Interskate Self Storage'
                                    });
                                }
                            }}
                            className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
                        >
                            See Household Rates
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </a>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Moving Made Simple</h2>
                        <p className="text-xl text-gray-700 leading-relaxed mb-6">
                            Moving is hard enough without having to haul heavy furniture down long hallways or wait for slow freight elevators.
                        </p>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            At <strong>Interskate Self Storage</strong>, we specialize in <strong>Drive-Up Household Storage</strong>. This means you can pull your moving truck, van, or trailer directly up to the door of your unit.
                        </p>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Choose Drive-Up Storage?
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="bg-white p-6 rounded-lg shadow-sm flex items-start">
                                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mr-4 mt-1" />
                                <span className="text-lg text-gray-700 font-medium">{benefit}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Size Recommendations */}
            <section className="py-16 bg-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                        Ideal Unit Sizes for Households
                    </h2>
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                        <div className="hidden md:grid grid-cols-3 bg-green-600 text-white p-4 font-bold text-lg">
                            <div>Unit Size</div>
                            <div className="col-span-2">Best For</div>
                        </div>
                        {unitSizes.map((item, index) => (
                            <div key={index} className={`grid grid-cols-1 md:grid-cols-3 p-6 border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <div className="font-bold text-xl text-green-800 mb-2 md:mb-0">{item.size}</div>
                                <div className="col-span-2">
                                    <span className="font-bold text-gray-900 block mb-1">{item.title}</span>
                                    <span className="text-gray-600">{item.description}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HouseholdStorage;
